import React, { useEffect, useState, useCallback } from "react";
import {
  Box, Card, CardContent, Chip, CircularProgress, Container, FormControl,
  IconButton, InputLabel, MenuItem, Paper, Select, Table, TableBody,
  TableCell, TableContainer, TableHead, TableRow, TextField, Tooltip,
  Typography, Tab, Tabs, Skeleton,
} from "@mui/material";
import TrendingUpIcon from "@mui/icons-material/TrendingUp";
import TrendingDownIcon from "@mui/icons-material/TrendingDown";
import AccountBalanceWalletOutlinedIcon from "@mui/icons-material/AccountBalanceWalletOutlined";
import MonetizationOnOutlinedIcon from "@mui/icons-material/MonetizationOnOutlined";
import ReceiptLongOutlinedIcon from "@mui/icons-material/ReceiptLongOutlined";
import PercentOutlinedIcon from "@mui/icons-material/PercentOutlined";
import EmojiEventsOutlinedIcon from "@mui/icons-material/EmojiEventsOutlined";
import CategoryOutlinedIcon from "@mui/icons-material/CategoryOutlined";
import WarningAmberOutlinedIcon from "@mui/icons-material/WarningAmberOutlined";
import FilterListIcon from "@mui/icons-material/FilterList";
import RefreshIcon from "@mui/icons-material/Refresh";
import {
  Chart as ChartJS, CategoryScale, LinearScale, BarElement, PointElement,
  LineElement, ArcElement, Filler, Tooltip as ChartTooltip, Legend,
} from "chart.js";
import { Bar, Line, Doughnut } from "react-chartjs-2";
import {
  DashboardData, DashboardFilters, FilterOptions, VendorPerformanceRow,
  ItemProfitabilityRow, fetchDashboard, fetchFilterOptions,
  fetchVendorPerformance, fetchItemProfitability,
} from "./vendorIntelligenceApi";

ChartJS.register(
  CategoryScale, LinearScale, BarElement, PointElement, LineElement,
  ArcElement, Filler, ChartTooltip, Legend
);

/* ── Theme ──────────────────────────────────────────────────────────────── */
const T = {
  navy: "#0d1b2a", navyLight: "#1b2e4a", gold: "#c9a96e", goldLight: "#f5eed9",
  white: "#fff", cream: "#faf8f4", bg: "#f7f5f1",
  slate: "#5c6b7e", slateLt: "#9ca8b7", line: "#eeebe6",
  green: "#2d6a35", greenBg: "#edf7ee", greenLt: "#4ade80",
  red: "#9b2c2c", redBg: "#fef2f2",
  blue: "#1e40af", blueBg: "#eff6ff",
  purple: "#6d28d9", purpleBg: "#f5f3ff",
  orange: "#c2410c", orangeBg: "#fff7ed",
};

const fmtCAD = (n: number) =>
  n >= 1000
    ? `$${(n / 1000).toFixed(1)}k`
    : `$${n.toLocaleString("en-CA", { minimumFractionDigits: 0, maximumFractionDigits: 0 })}`;
const fmtFull = (n: number) =>
  `$${n.toLocaleString("en-CA", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

const PRICING_LABELS: Record<string, string> = {
  fixed: "Per Event", per_guest: "Per Guest", per_hour: "Per Hour", per_unit: "Per Unit",
};

/* ── KPI Card ────────────────────────────────────────────────────────── */
const KpiCard: React.FC<{
  label: string; value: string; sub?: string; icon: React.ReactNode;
  color: string; bgColor: string;
}> = ({ label, value, sub, icon, color, bgColor }) => (
  <Card sx={{
    borderRadius: 3, border: `1px solid ${T.line}`, boxShadow: "0 1px 4px rgba(0,0,0,0.04)",
    transition: "transform 0.15s, box-shadow 0.15s",
    "&:hover": { transform: "translateY(-2px)", boxShadow: "0 6px 20px rgba(0,0,0,0.06)" },
  }}>
    <CardContent sx={{ p: "20px !important" }}>
      <Box sx={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between" }}>
        <Box>
          <Typography sx={{ fontSize: 11, fontWeight: 600, color: T.slateLt, textTransform: "uppercase", letterSpacing: 0.8 }}>
            {label}
          </Typography>
          <Typography sx={{ fontSize: 26, fontWeight: 700, color: T.navy, mt: 0.5, lineHeight: 1.1 }}>
            {value}
          </Typography>
          {sub && (
            <Typography sx={{ fontSize: 11, color: T.slate, mt: 0.5 }}>{sub}</Typography>
          )}
        </Box>
        <Box sx={{
          width: 44, height: 44, borderRadius: 2.5, bgcolor: bgColor,
          display: "flex", alignItems: "center", justifyContent: "center",
        }}>
          {React.cloneElement(icon as React.ReactElement, { sx: { fontSize: 22, color } })}
        </Box>
      </Box>
    </CardContent>
  </Card>
);

/* ── Chart Card wrapper ───────────────────────────────────────────────── */
const ChartCard: React.FC<{ title: string; children: React.ReactNode; height?: number }> = ({ title, children, height = 300 }) => (
  <Paper sx={{
    p: 3, borderRadius: 3, border: `1px solid ${T.line}`,
    boxShadow: "0 1px 4px rgba(0,0,0,0.04)", bgcolor: T.white,
  }}>
    <Typography sx={{ fontSize: 14, fontWeight: 700, color: T.navy, mb: 2 }}>{title}</Typography>
    <Box sx={{ height }}>{children}</Box>
  </Paper>
);

/* ═══════════════════════════════════════════════════════════════════════════
   MAIN DASHBOARD COMPONENT
   ═══════════════════════════════════════════════════════════════════════════ */
const VendorProfitDashboard: React.FC = () => {
  const [data, setData] = useState<DashboardData | null>(null);
  const [vendorPerf, setVendorPerf] = useState<VendorPerformanceRow[]>([]);
  const [itemProfit, setItemProfit] = useState<ItemProfitabilityRow[]>([]);
  const [filterOpts, setFilterOpts] = useState<FilterOptions | null>(null);
  const [filters, setFilters] = useState<DashboardFilters>({});
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState(0);
  const [showFilters, setShowFilters] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [d, vp, ip] = await Promise.all([
        fetchDashboard(filters),
        fetchVendorPerformance(filters),
        fetchItemProfitability(filters),
      ]);
      setData(d);
      setVendorPerf(vp);
      setItemProfit(ip);
    } catch (e) { console.error("Dashboard load error:", e); }
    setLoading(false);
  }, [filters]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => { fetchFilterOptions().then(setFilterOpts).catch(console.error); }, []);

  const m = data?.metrics;

  /* ── Chart configs ────────────────────────────────────────────────────── */
  const profitByVendorChart = data ? {
    labels: data.profit_by_vendor.map(d => d.vendor_name || ""),
    datasets: [
      { label: "Revenue", data: data.profit_by_vendor.map(d => d.revenue), backgroundColor: "rgba(30,64,175,0.7)", borderRadius: 6 },
      { label: "Cost", data: data.profit_by_vendor.map(d => d.cost), backgroundColor: "rgba(201,169,110,0.5)", borderRadius: 6 },
      { label: "Profit", data: data.profit_by_vendor.map(d => d.profit), backgroundColor: "rgba(45,106,53,0.7)", borderRadius: 6 },
    ],
  } : null;

  const profitByServiceChart = data ? {
    labels: data.profit_by_service.map(d => d.service_name || ""),
    datasets: [{
      data: data.profit_by_service.map(d => d.profit),
      backgroundColor: ["#1e40af", "#c9a96e", "#2d6a35", "#6d28d9", "#c2410c", "#0891b2", "#db2777"],
      borderWidth: 0,
    }],
  } : null;

  const trendChart = data ? {
    labels: data.revenue_cost_trend.labels,
    datasets: [
      {
        label: "Revenue", data: data.revenue_cost_trend.revenue,
        borderColor: "#1e40af", backgroundColor: "rgba(30,64,175,0.08)",
        fill: true, tension: 0.4, pointRadius: 3,
      },
      {
        label: "Cost", data: data.revenue_cost_trend.cost,
        borderColor: "#c9a96e", backgroundColor: "rgba(201,169,110,0.08)",
        fill: true, tension: 0.4, pointRadius: 3,
      },
      {
        label: "Profit", data: data.revenue_cost_trend.profit,
        borderColor: "#2d6a35", backgroundColor: "rgba(45,106,53,0.08)",
        fill: true, tension: 0.4, pointRadius: 3,
      },
    ],
  } : null;

  const marginByVendorChart = data ? {
    labels: data.margin_by_vendor.map(d => d.vendor_name || ""),
    datasets: [{
      label: "Margin %",
      data: data.margin_by_vendor.map(d => d.margin || 0),
      backgroundColor: data.margin_by_vendor.map(d =>
        (d.margin || 0) >= 40 ? "rgba(45,106,53,0.7)" :
        (d.margin || 0) >= 20 ? "rgba(201,169,110,0.7)" : "rgba(155,44,44,0.7)"
      ),
      borderRadius: 6,
    }],
  } : null;

  const chartOpts = (type: "bar" | "line" | "hbar") => ({
    responsive: true, maintainAspectRatio: false,
    indexAxis: type === "hbar" ? "y" as const : "x" as const,
    plugins: {
      legend: { display: type === "line", position: "top" as const, labels: { font: { size: 11 }, usePointStyle: true } },
      tooltip: {
        backgroundColor: T.navy, titleFont: { size: 12 }, bodyFont: { size: 11 },
        callbacks: {
          label: (ctx: any) => type === "hbar"
            ? `${ctx.parsed.x.toFixed(1)}%`
            : `${ctx.dataset.label}: ${fmtFull(ctx.parsed.y)}`,
        },
      },
    },
    scales: {
      x: { grid: { display: false }, ticks: { font: { size: 10 }, maxRotation: 45 } },
      y: { grid: { color: "#f0ede8" }, ticks: { font: { size: 10 }, callback: (v: any) => type === "hbar" ? `${v}%` : fmtCAD(v) } },
    },
  });

  return (
    <Container maxWidth="xl" sx={{ py: 4 }}>
      {/* ── Header ──────────────────────────────────────────────────────── */}
      <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 3 }}>
        <Box>
          <Typography sx={{ fontSize: 24, fontWeight: 800, color: T.navy, letterSpacing: -0.5 }}>
            Vendor Profit Dashboard
          </Typography>
          <Typography sx={{ fontSize: 13, color: T.slate, mt: 0.3 }}>
            Profitability analytics across all vendors, services, and events
          </Typography>
        </Box>
        <Box sx={{ display: "flex", gap: 1 }}>
          <Tooltip title="Toggle Filters">
            <IconButton onClick={() => setShowFilters(p => !p)} sx={{
              bgcolor: showFilters ? T.goldLight : T.cream, borderRadius: 2,
              border: `1px solid ${T.line}`, "&:hover": { bgcolor: T.goldLight },
            }}>
              <FilterListIcon sx={{ fontSize: 18, color: T.navy }} />
            </IconButton>
          </Tooltip>
          <Tooltip title="Refresh">
            <IconButton onClick={load} sx={{
              bgcolor: T.cream, borderRadius: 2, border: `1px solid ${T.line}`,
              "&:hover": { bgcolor: T.goldLight },
            }}>
              <RefreshIcon sx={{ fontSize: 18, color: T.navy }} />
            </IconButton>
          </Tooltip>
        </Box>
      </Box>

      {/* ── Filters ─────────────────────────────────────────────────────── */}
      {showFilters && (
        <Paper sx={{ p: 2.5, mb: 3, borderRadius: 3, border: `1px solid ${T.line}`, bgcolor: T.cream }}>
          <Box sx={{ display: "flex", gap: 2, flexWrap: "wrap", alignItems: "center" }}>
            <TextField size="small" type="date" label="From" InputLabelProps={{ shrink: true }}
              value={filters.date_from || ""} onChange={e => setFilters(p => ({ ...p, date_from: e.target.value }))}
              sx={{ minWidth: 150, "& .MuiInputBase-root": { fontSize: 12, borderRadius: 2 } }}
            />
            <TextField size="small" type="date" label="To" InputLabelProps={{ shrink: true }}
              value={filters.date_to || ""} onChange={e => setFilters(p => ({ ...p, date_to: e.target.value }))}
              sx={{ minWidth: 150, "& .MuiInputBase-root": { fontSize: 12, borderRadius: 2 } }}
            />
            <FormControl size="small" sx={{ minWidth: 150 }}>
              <InputLabel sx={{ fontSize: 12 }}>Event Type</InputLabel>
              <Select value={filters.event_type || ""} label="Event Type"
                onChange={e => setFilters(p => ({ ...p, event_type: e.target.value as string }))}
                sx={{ fontSize: 12, borderRadius: 2 }}>
                <MenuItem value=""><em>All</em></MenuItem>
                {filterOpts?.event_types.map(t => <MenuItem key={t} value={t}>{t}</MenuItem>)}
              </Select>
            </FormControl>
            <FormControl size="small" sx={{ minWidth: 150 }}>
              <InputLabel sx={{ fontSize: 12 }}>Vendor</InputLabel>
              <Select value={filters.vendor_id ?? ""} label="Vendor"
                onChange={e => setFilters(p => ({ ...p, vendor_id: e.target.value ? Number(e.target.value) : undefined }))}
                sx={{ fontSize: 12, borderRadius: 2 }}>
                <MenuItem value=""><em>All</em></MenuItem>
                {filterOpts?.vendors.map(v => <MenuItem key={v.id} value={v.id}>{v.name}</MenuItem>)}
              </Select>
            </FormControl>
            <FormControl size="small" sx={{ minWidth: 150 }}>
              <InputLabel sx={{ fontSize: 12 }}>Service</InputLabel>
              <Select value={filters.service_name || ""} label="Service"
                onChange={e => setFilters(p => ({ ...p, service_name: e.target.value as string }))}
                sx={{ fontSize: 12, borderRadius: 2 }}>
                <MenuItem value=""><em>All</em></MenuItem>
                {filterOpts?.services.map(s => <MenuItem key={s} value={s}>{s}</MenuItem>)}
              </Select>
            </FormControl>
            <FormControl size="small" sx={{ minWidth: 130 }}>
              <InputLabel sx={{ fontSize: 12 }}>Pricing Type</InputLabel>
              <Select value={filters.pricing_type || ""} label="Pricing Type"
                onChange={e => setFilters(p => ({ ...p, pricing_type: e.target.value as string }))}
                sx={{ fontSize: 12, borderRadius: 2 }}>
                <MenuItem value=""><em>All</em></MenuItem>
                {filterOpts?.pricing_types.map(t => <MenuItem key={t} value={t}>{PRICING_LABELS[t] || t}</MenuItem>)}
              </Select>
            </FormControl>
            <Chip label="Clear" size="small" onClick={() => setFilters({})}
              sx={{ bgcolor: T.redBg, color: T.red, fontWeight: 600, fontSize: 11, cursor: "pointer" }}
            />
          </Box>
        </Paper>
      )}

      {loading && !data ? (
        <Box sx={{ display: "grid", gridTemplateColumns: "repeat(4, 1fr)", gap: 2.5, mb: 3 }}>
          {[1,2,3,4].map(i => <Skeleton key={i} variant="rounded" height={110} sx={{ borderRadius: 3 }} />)}
        </Box>
      ) : !m ? (
        <Paper sx={{ p: 6, textAlign: "center", borderRadius: 3, border: `1px solid ${T.line}` }}>
          <Typography sx={{ fontSize: 16, color: T.slate }}>No data available. Assign vendors to bookings to see analytics.</Typography>
        </Paper>
      ) : (
        <>
          {/* ── KPI Cards ────────────────────────────────────────────────── */}
          <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr 1fr", lg: "repeat(4, 1fr)" }, gap: 2.5, mb: 3 }}>
            <KpiCard label="Total Vendor Spend" value={fmtCAD(m.total_vendor_spend)}
              icon={<AccountBalanceWalletOutlinedIcon />} color={T.orange} bgColor={T.orangeBg} />
            <KpiCard label="Total Revenue" value={fmtCAD(m.total_vendor_revenue)}
              icon={<MonetizationOnOutlinedIcon />} color={T.blue} bgColor={T.blueBg} />
            <KpiCard label="Total Profit" value={fmtCAD(m.total_profit)}
              sub={`${m.total_items_used} items processed`}
              icon={<TrendingUpIcon />} color={T.green} bgColor={T.greenBg} />
            <KpiCard label="Avg Margin" value={`${m.avg_margin_percent}%`}
              icon={<PercentOutlinedIcon />} color={T.purple} bgColor={T.purpleBg} />
          </Box>

          {/* ── Highlight Cards ──────────────────────────────────────────── */}
          <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", md: "repeat(3, 1fr)" }, gap: 2.5, mb: 3 }}>
            {m.most_profitable_vendor && (
              <Card sx={{ borderRadius: 3, border: `1px solid ${T.line}`, boxShadow: "none" }}>
                <CardContent sx={{ p: "16px !important", display: "flex", alignItems: "center", gap: 2 }}>
                  <Box sx={{ width: 40, height: 40, borderRadius: 2, bgcolor: T.goldLight, display: "flex", alignItems: "center", justifyContent: "center" }}>
                    <EmojiEventsOutlinedIcon sx={{ fontSize: 20, color: T.gold }} />
                  </Box>
                  <Box>
                    <Typography sx={{ fontSize: 10, fontWeight: 600, color: T.slateLt, textTransform: "uppercase" }}>Most Profitable Vendor</Typography>
                    <Typography sx={{ fontSize: 14, fontWeight: 700, color: T.navy }}>{m.most_profitable_vendor.name}</Typography>
                    <Typography sx={{ fontSize: 11, color: T.green, fontWeight: 600 }}>{fmtFull(m.most_profitable_vendor.profit)} profit</Typography>
                  </Box>
                </CardContent>
              </Card>
            )}
            {m.most_profitable_service && (
              <Card sx={{ borderRadius: 3, border: `1px solid ${T.line}`, boxShadow: "none" }}>
                <CardContent sx={{ p: "16px !important", display: "flex", alignItems: "center", gap: 2 }}>
                  <Box sx={{ width: 40, height: 40, borderRadius: 2, bgcolor: T.blueBg, display: "flex", alignItems: "center", justifyContent: "center" }}>
                    <CategoryOutlinedIcon sx={{ fontSize: 20, color: T.blue }} />
                  </Box>
                  <Box>
                    <Typography sx={{ fontSize: 10, fontWeight: 600, color: T.slateLt, textTransform: "uppercase" }}>Most Profitable Service</Typography>
                    <Typography sx={{ fontSize: 14, fontWeight: 700, color: T.navy }}>{m.most_profitable_service.name}</Typography>
                    <Typography sx={{ fontSize: 11, color: T.blue, fontWeight: 600 }}>{fmtFull(m.most_profitable_service.profit)} profit</Typography>
                  </Box>
                </CardContent>
              </Card>
            )}
            {m.lowest_margin_vendor && (
              <Card sx={{ borderRadius: 3, border: `1px solid ${T.line}`, boxShadow: "none" }}>
                <CardContent sx={{ p: "16px !important", display: "flex", alignItems: "center", gap: 2 }}>
                  <Box sx={{ width: 40, height: 40, borderRadius: 2, bgcolor: T.redBg, display: "flex", alignItems: "center", justifyContent: "center" }}>
                    <WarningAmberOutlinedIcon sx={{ fontSize: 20, color: T.red }} />
                  </Box>
                  <Box>
                    <Typography sx={{ fontSize: 10, fontWeight: 600, color: T.slateLt, textTransform: "uppercase" }}>Lowest Margin Vendor</Typography>
                    <Typography sx={{ fontSize: 14, fontWeight: 700, color: T.navy }}>{m.lowest_margin_vendor.name}</Typography>
                    <Typography sx={{ fontSize: 11, color: T.red, fontWeight: 600 }}>{m.lowest_margin_vendor.margin}% margin</Typography>
                  </Box>
                </CardContent>
              </Card>
            )}
          </Box>

          {/* ── Charts ───────────────────────────────────────────────────── */}
          <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", lg: "2fr 1fr" }, gap: 2.5, mb: 3 }}>
            <ChartCard title="Profit by Vendor">
              {profitByVendorChart && <Bar data={profitByVendorChart} options={chartOpts("bar") as any} />}
            </ChartCard>
            <ChartCard title="Profit by Service Category">
              {profitByServiceChart && (
                <Doughnut data={profitByServiceChart} options={{
                  responsive: true, maintainAspectRatio: false, cutout: "60%",
                  plugins: {
                    legend: { position: "bottom", labels: { font: { size: 10 }, padding: 12, usePointStyle: true } },
                    tooltip: { backgroundColor: T.navy, callbacks: { label: (ctx: any) => `${ctx.label}: ${fmtFull(ctx.parsed)}` } },
                  },
                }} />
              )}
            </ChartCard>
          </Box>

          <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", lg: "1fr 1fr" }, gap: 2.5, mb: 3 }}>
            <ChartCard title="Revenue vs Cost Trend">
              {trendChart && <Line data={trendChart} options={chartOpts("line") as any} />}
            </ChartCard>
            <ChartCard title="Margin % by Vendor" height={300}>
              {marginByVendorChart && <Bar data={marginByVendorChart} options={chartOpts("hbar") as any} />}
            </ChartCard>
          </Box>

          {/* ── Top Items ────────────────────────────────────────────────── */}
          {data && data.top_profitable_items.length > 0 && (
            <Paper sx={{ p: 3, borderRadius: 3, border: `1px solid ${T.line}`, boxShadow: "0 1px 4px rgba(0,0,0,0.04)", mb: 3 }}>
              <Typography sx={{ fontSize: 14, fontWeight: 700, color: T.navy, mb: 2 }}>
                Top 10 Most Profitable Items
              </Typography>
              <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", md: "repeat(2, 1fr)", lg: "repeat(5, 1fr)" }, gap: 1.5 }}>
                {data.top_profitable_items.map((item, i) => (
                  <Card key={i} sx={{
                    borderRadius: 2.5, border: `1px solid ${T.line}`, boxShadow: "none",
                    bgcolor: i < 3 ? T.cream : T.white,
                  }}>
                    <CardContent sx={{ p: "14px !important" }}>
                      <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1 }}>
                        <Box sx={{
                          width: 22, height: 22, borderRadius: "50%", fontSize: 10, fontWeight: 700,
                          display: "flex", alignItems: "center", justifyContent: "center",
                          bgcolor: i < 3 ? T.gold : T.slateLt, color: T.white,
                        }}>
                          {i + 1}
                        </Box>
                        <Typography sx={{ fontSize: 12, fontWeight: 700, color: T.navy, lineHeight: 1.2, flex: 1 }} noWrap>
                          {item.item_name}
                        </Typography>
                      </Box>
                      <Typography sx={{ fontSize: 10, color: T.slate, mb: 0.5 }} noWrap>{item.vendor_name}</Typography>
                      <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "baseline" }}>
                        <Typography sx={{ fontSize: 16, fontWeight: 700, color: T.green }}>{fmtFull(item.profit)}</Typography>
                        <Chip label={`${item.margin}%`} size="small" sx={{
                          height: 20, fontSize: 10, fontWeight: 700,
                          bgcolor: item.margin >= 40 ? T.greenBg : item.margin >= 20 ? T.goldLight : T.redBg,
                          color: item.margin >= 40 ? T.green : item.margin >= 20 ? T.gold : T.red,
                        }} />
                      </Box>
                      <Typography sx={{ fontSize: 10, color: T.slateLt, mt: 0.3 }}>
                        Used {item.times_used}x &middot; {PRICING_LABELS[item.pricing_type] || item.pricing_type}
                      </Typography>
                    </CardContent>
                  </Card>
                ))}
              </Box>
            </Paper>
          )}

          {/* ── Tables ───────────────────────────────────────────────────── */}
          <Paper sx={{ borderRadius: 3, border: `1px solid ${T.line}`, boxShadow: "0 1px 4px rgba(0,0,0,0.04)", overflow: "hidden" }}>
            <Tabs value={tab} onChange={(_, v) => setTab(v)} sx={{
              borderBottom: `1px solid ${T.line}`, px: 2,
              "& .MuiTab-root": { fontSize: 12, fontWeight: 600, textTransform: "none", minHeight: 48, color: T.slate },
              "& .Mui-selected": { color: T.navy },
              "& .MuiTabs-indicator": { bgcolor: T.gold, height: 2 },
            }}>
              <Tab label="Vendor Performance" />
              <Tab label="Item Profitability" />
            </Tabs>

            {tab === 0 && (
              <TableContainer>
                <Table size="small">
                  <TableHead>
                    <TableRow sx={{ bgcolor: T.cream }}>
                      {["Vendor", "Services", "Revenue", "Cost", "Profit", "Margin %", "Jobs"].map(h => (
                        <TableCell key={h} sx={{ fontSize: 11, fontWeight: 700, color: T.navy, py: 1.5, borderColor: T.line }}>{h}</TableCell>
                      ))}
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {vendorPerf.length === 0 ? (
                      <TableRow><TableCell colSpan={7} sx={{ textAlign: "center", py: 4, color: T.slate, fontSize: 13 }}>No vendor performance data available.</TableCell></TableRow>
                    ) : vendorPerf.map((v, i) => (
                      <TableRow key={i} sx={{ "&:hover": { bgcolor: T.cream }, transition: "background 0.1s" }}>
                        <TableCell sx={{ fontSize: 12, fontWeight: 600, color: T.navy, borderColor: T.line }}>{v.vendor_name}</TableCell>
                        <TableCell sx={{ fontSize: 11, color: T.slate, borderColor: T.line, maxWidth: 200 }}>
                          <Typography noWrap sx={{ fontSize: 11 }}>{v.services}</Typography>
                        </TableCell>
                        <TableCell sx={{ fontSize: 12, fontWeight: 500, borderColor: T.line }}>{fmtFull(v.revenue)}</TableCell>
                        <TableCell sx={{ fontSize: 12, fontWeight: 500, borderColor: T.line }}>{fmtFull(v.cost)}</TableCell>
                        <TableCell sx={{ fontSize: 12, fontWeight: 700, color: v.profit >= 0 ? T.green : T.red, borderColor: T.line }}>
                          {fmtFull(v.profit)}
                        </TableCell>
                        <TableCell sx={{ borderColor: T.line }}>
                          <Chip label={`${v.margin}%`} size="small" sx={{
                            height: 22, fontSize: 11, fontWeight: 700,
                            bgcolor: v.margin >= 40 ? T.greenBg : v.margin >= 20 ? T.goldLight : T.redBg,
                            color: v.margin >= 40 ? T.green : v.margin >= 20 ? "#8b6914" : T.red,
                          }} />
                        </TableCell>
                        <TableCell sx={{ fontSize: 12, fontWeight: 600, color: T.navy, borderColor: T.line }}>{v.jobs_count}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            )}

            {tab === 1 && (
              <TableContainer>
                <Table size="small">
                  <TableHead>
                    <TableRow sx={{ bgcolor: T.cream }}>
                      {["Item", "Vendor", "Service", "Pricing", "Revenue", "Cost", "Profit", "Margin %", "Used"].map(h => (
                        <TableCell key={h} sx={{ fontSize: 11, fontWeight: 700, color: T.navy, py: 1.5, borderColor: T.line }}>{h}</TableCell>
                      ))}
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {itemProfit.length === 0 ? (
                      <TableRow><TableCell colSpan={9} sx={{ textAlign: "center", py: 4, color: T.slate, fontSize: 13 }}>No item profitability data available.</TableCell></TableRow>
                    ) : itemProfit.map((item, i) => (
                      <TableRow key={i} sx={{ "&:hover": { bgcolor: T.cream }, transition: "background 0.1s" }}>
                        <TableCell sx={{ fontSize: 12, fontWeight: 600, color: T.navy, borderColor: T.line, maxWidth: 160 }}>
                          <Typography noWrap sx={{ fontSize: 12, fontWeight: 600 }}>{item.item_name}</Typography>
                        </TableCell>
                        <TableCell sx={{ fontSize: 11, color: T.slate, borderColor: T.line }}>{item.vendor_name}</TableCell>
                        <TableCell sx={{ fontSize: 11, color: T.slate, borderColor: T.line }}>{item.service_name}</TableCell>
                        <TableCell sx={{ borderColor: T.line }}>
                          <Chip label={PRICING_LABELS[item.pricing_type] || item.pricing_type} size="small"
                            sx={{ height: 20, fontSize: 10, fontWeight: 600, bgcolor: T.blueBg, color: T.blue }} />
                        </TableCell>
                        <TableCell sx={{ fontSize: 12, borderColor: T.line }}>{fmtFull(item.revenue)}</TableCell>
                        <TableCell sx={{ fontSize: 12, borderColor: T.line }}>{fmtFull(item.cost)}</TableCell>
                        <TableCell sx={{ fontSize: 12, fontWeight: 700, color: item.profit >= 0 ? T.green : T.red, borderColor: T.line }}>
                          {fmtFull(item.profit)}
                        </TableCell>
                        <TableCell sx={{ borderColor: T.line }}>
                          <Chip label={`${item.margin}%`} size="small" sx={{
                            height: 22, fontSize: 11, fontWeight: 700,
                            bgcolor: item.margin >= 40 ? T.greenBg : item.margin >= 20 ? T.goldLight : T.redBg,
                            color: item.margin >= 40 ? T.green : item.margin >= 20 ? "#8b6914" : T.red,
                          }} />
                        </TableCell>
                        <TableCell sx={{ fontSize: 12, fontWeight: 600, color: T.navy, borderColor: T.line }}>{item.times_used}x</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            )}
          </Paper>
        </>
      )}
    </Container>
  );
};

export default VendorProfitDashboard;
